import { type KeyboardEvent, useCallback, useRef, useState } from 'react';

import { debounce } from 'ts-debounce';
import { RangeSlider as TroikaRangeSlider } from './components/troikaSlider/TroikaSlider';

import type { NumberRange, OpenNumberRange } from '@/interfaces';
import { LinkButtonTextField } from './components/linkButtonTextField/LinkButtonTextField';

export interface RangeSliderStateProps {
    id: string;
    label: string;
    min: number;
    max: number;
    stepSize: number;
    initialValues?: OpenNumberRange;
    resetOnDefault?: boolean;
    metric?: string;
    displayLabel?: boolean;
}

export interface RangeSliderDispatchProps {
    onChange: (values?: OpenNumberRange) => void;
    triggerFetch: () => void;
}

export type RangeSliderProps = RangeSliderStateProps & RangeSliderDispatchProps;

export const stepsToRange = (steps: NumberRange, stepSize: number): NumberRange => [
    Math.round(steps[0] * stepSize),
    Math.round(steps[1] * stepSize),
];
export const rangeToSteps = (price: NumberRange, stepSize: number): NumberRange => [price[0] / stepSize, price[1] / stepSize];

export const minSteps = (min: number, stepSize: number): number => {
    const steps = rangeToSteps([min, min], stepSize);
    return steps[0];
};

export const maxSteps = (max: number, stepSize: number): number => {
    const steps = rangeToSteps([max, max], stepSize);
    return steps[1];
};

export const RangeSlider = ({
    id,
    label,
    min,
    max,
    stepSize,
    initialValues,
    resetOnDefault = false,
    metric = '',
    onChange,
    triggerFetch,
    displayLabel = true,
}: RangeSliderProps) => {
    const [showInput, setShowInput] = useState(false);
    const [values, setValues] = useState<NumberRange>(initialValues ? [initialValues[0], initialValues[1] || max] : [min, max]);
    const inputDiv = useRef<HTMLDivElement>();

    const updateState = (range: NumberRange): void => {
        if (!resetOnDefault || range[0] !== min || range[1] !== max) {
            const actualRange: OpenNumberRange = [range[0], range[1] === max ? undefined : range[1]];
            onChange(actualRange);
        } else {
            onChange(undefined);
        }
        setShowInput(false);
        triggerFetch();
    };
    const debouncedUpdateState = useCallback(debounce(updateState, 450), []);

    const onSliderChange = (steps: NumberRange): void => {
        const newValues = stepsToRange(steps, stepSize);
        debouncedUpdateState(newValues);
        setValues(newValues);
    };

    const onBlur = (): void => {
        setTimeout(() => {
            if (!inputDiv.current?.contains(document.activeElement)) {
                updateState(values);
            }
        }, 0);
    };

    const onClick = (): void => {
        setShowInput(true);
    };

    const clamp = (value: number): number => Math.min(Math.max(value, min), max);

    const onTextChange = ({ low = values[0], high = values[1] }: { low?: number; high?: number }): void => {
        setValues([clamp(low), clamp(high)]);
    };

    const restrictKeys = (event: KeyboardEvent): void => {
        const regex = /[0-9]+/g;
        if (!regex.test(event.key)) {
            event.preventDefault();
        }
    };

    return (
        <div id={id} className="u-mv16">
            {displayLabel && <h3 className="u-t5">{label}</h3>}
            <div className="grid grid--cols1to2" ref={inputDiv}>
                <LinkButtonTextField
                    id={`${id}-Min`}
                    label={`Minimum ${label}`}
                    type="number"
                    value={values[0].toString()}
                    metric={metric}
                    showInput={showInput}
                    onClick={onClick}
                    onTextChange={(value): void => onTextChange({ low: ~~value })}
                    onBlur={onBlur}
                    onKeyPress={restrictKeys}
                    data-testid={`RangeSliderText-Min-${id}`}
                />
                <LinkButtonTextField
                    id={`${id}-Max`}
                    label={`Maks ${label}`}
                    type="number"
                    prefix={!values[1] || values[1] >= max ? 'over' : undefined}
                    value={values[1].toString()}
                    metric={metric}
                    showInput={showInput}
                    onClick={onClick}
                    onTextChange={(value): void => onTextChange({ high: ~~value })}
                    onBlur={onBlur}
                    onKeyPress={restrictKeys}
                    floatRight
                    data-testid={`RangeSliderText-Max-${id}`}
                />
            </div>
            <div data-testid={`RangeSliderWrapper-${id}`}>
                <TroikaRangeSlider
                    aria-label={[`Minimum ${label}`, `Maks ${label}`]}
                    onChange={onSliderChange}
                    onInput={onSliderChange}
                    value={rangeToSteps([values[0], values[1]], stepSize)}
                    min={minSteps(min, stepSize)}
                    max={maxSteps(max, stepSize)}
                    data-testid={`RangeSlider-${id}`}
                />
            </div>
        </div>
    );
};
